/**
 * Caption cues for an edit: from the video's transcript words (source time),
 * placed on the result's timeline through the edit's segments, wrapped to the
 * caption style's line length; or from a caption file the creator uploaded.
 *
 * A word that falls in a cut stretch is dropped; a cue never crosses a segment
 * boundary (the picture jumps there, so should the caption); a sped-up stretch
 * shows its captions faster.
 *
 * Copied unchanged into the app (src/utils/editing/captions.ts) for the
 * preview; a test keeps the two in step, so it stays TS 4.9 / ES5 friendly.
 */

import type { CaptionsStyle, EditList } from './editList';
import { segmentPlacements } from './editList';

/** A word of a cue, on the result's timeline (what the highlight look lights). */
export interface CueWord {
  text: string;
  startMs: number;
  endMs: number;
  /** Which of the cue's lines it's on. */
  line: number;
}

/** One caption on the result's timeline, its lines already wrapped. */
export interface CaptionCue {
  startMs: number;
  endMs: number;
  lines: string[];
  /** The cue's words with their times, when the cue came from transcript words (not from a file). */
  words?: CueWord[];
}

export interface TimedWord {
  text: string;
  /** Source seconds (as the transcript stores them). */
  start: number;
  end: number;
}

/** Longest a cue stays up, and the longest silence a cue may bridge. */
const MAX_CUE_MS = 6000;
const MAX_GAP_MS = 1200;
const MIN_CUE_MS = 600;

interface PlacedWord {
  text: string;
  startMs: number;
  endMs: number;
  segmentIndex: number;
}

/** Words on the output timeline, in order, with the segment each plays in. */
export function placeWords(words: TimedWord[], list: Pick<EditList, 'segments' | 'sourceDurationMs'>): PlacedWord[] {
  const placements = segmentPlacements(list);
  const placed: PlacedWord[] = [];
  placements.forEach(({ segment, startMs }, segmentIndex) => {
    words.forEach((word) => {
      const wordStartMs = word.start * 1000;
      const wordEndMs = word.end * 1000;
      // A word belongs to the segment its middle falls in.
      const middle = (wordStartMs + wordEndMs) / 2;
      if (middle < segment.inMs || middle >= segment.outMs) return;
      const text = word.text.trim();
      if (!text) return;
      const from = Math.max(segment.inMs, wordStartMs);
      const to = Math.min(segment.outMs, Math.max(wordEndMs, from + 1));
      placed.push({
        text,
        startMs: Math.round(startMs + (from - segment.inMs) / segment.speed),
        endMs: Math.round(startMs + (to - segment.inMs) / segment.speed),
        segmentIndex,
      });
    });
  });
  return placed;
}

/** Greedy word wrap: the line each word lands on, lines of at most `maxChars`, a longer word on its own line. */
export function wrapWordLines(words: string[], maxChars: number): number[] {
  const lineOf: number[] = [];
  let line = 0;
  let length = 0;
  words.forEach((word) => {
    if (length === 0) {
      length = word.length;
    } else if (length + 1 + word.length <= maxChars) {
      length += 1 + word.length;
    } else {
      line += 1;
      length = word.length;
    }
    lineOf.push(line);
  });
  return lineOf;
}

/** Greedy word wrap: lines of at most `maxChars`, a word longer than that on its own line. */
export function wrapLines(words: string[], maxChars: number): string[] {
  const lineOf = wrapWordLines(words, maxChars);
  const lines: string[] = [];
  words.forEach((word, index) => {
    const line = lineOf[index];
    lines[line] = lines[line] ? `${lines[line]} ${word}` : word;
  });
  return lines;
}

/** The pop look: two or three words at a time, on one short line. */
const POP_MAX_WORDS = 3;
const POP_MAX_CHARS = 16;

/**
 * Cues from placed words: a cue closes at a segment boundary, at a long pause,
 * when it would run over `MAX_CUE_MS`, or when its words no longer fit the
 * allowed lines. Sentence ends (". ! ?") close a cue too, so captions read in
 * sentences where the pauses allow.
 */
export function cuesFromWords(
  words: TimedWord[],
  list: Pick<EditList, 'segments' | 'sourceDurationMs'>,
  style: Pick<CaptionsStyle, 'maxCharsPerLine' | 'maxLines'> & { look?: CaptionsStyle['look'] },
): CaptionCue[] {
  const placed = placeWords(words, list);
  const cues: CaptionCue[] = [];
  let group: PlacedWord[] = [];
  const pop = style.look === 'pop';
  const maxChars = pop ? POP_MAX_CHARS : style.maxCharsPerLine;
  const maxLines = pop ? 1 : style.maxLines;

  const flush = () => {
    if (group.length === 0) return;
    const texts = group.map((word) => word.text);
    const lineOf = wrapWordLines(texts, maxChars);
    const lines = wrapLines(texts, maxChars);
    const startMs = group[0].startMs;
    const last = group[group.length - 1];
    const cueWords: CueWord[] = group.map((word, index) => ({ text: word.text, startMs: word.startMs, endMs: word.endMs, line: lineOf[index] }));
    cues.push({ startMs, endMs: Math.max(last.endMs, startMs + (pop ? MIN_CUE_MS / 2 : MIN_CUE_MS)), lines, words: cueWords });
    group = [];
  };

  placed.forEach((word) => {
    if (group.length > 0) {
      const first = group[0];
      const previous = group[group.length - 1];
      const candidate = wrapLines([...group.map((w) => w.text), word.text], maxChars);
      const tooLong = word.endMs - first.startMs > MAX_CUE_MS;
      const pause = word.startMs - previous.endMs > MAX_GAP_MS;
      const newSegment = word.segmentIndex !== previous.segmentIndex;
      const sentenceEnd = /[.!?…]["”)]?$/.test(previous.text);
      const popFull = pop && (group.length >= POP_MAX_WORDS || /[,;:]$/.test(previous.text));
      if (newSegment || pause || tooLong || candidate.length > maxLines || sentenceEnd || popFull) flush();
    }
    group.push(word);
  });
  flush();

  // A cue never overlaps the next: it ends where the next starts if it ran long.
  for (let i = 0; i + 1 < cues.length; i += 1) {
    if (cues[i].endMs > cues[i + 1].startMs) cues[i].endMs = cues[i + 1].startMs;
  }
  // The result's end: nothing shows past it.
  const end = segmentPlacements(list).reduce((max, p) => Math.max(max, p.endMs), 0);
  return cues
    .map((cue) => ({ ...cue, endMs: Math.min(cue.endMs, end) }))
    .filter((cue) => cue.endMs > cue.startMs);
}

/** The word being said at an output time in a cue (the highlight look), or -1 before the first. */
export function spokenWordIndex(cue: CaptionCue, outputMs: number): number {
  if (!cue.words || cue.words.length === 0) return -1;
  let index = -1;
  for (let i = 0; i < cue.words.length; i += 1) {
    if (cue.words[i].startMs <= outputMs) index = i;
    else break;
  }
  return index;
}

// ---------------------------------------------------------------------------
// Caption files the creator uploaded (WebVTT or SRT)
// ---------------------------------------------------------------------------

function parseClock(value: string): number | null {
  const match = /^(?:(\d+):)?(\d{1,2}):(\d{2})[.,](\d{1,3})$/.exec(value.trim());
  if (!match) return null;
  const hours = match[1] ? Number(match[1]) : 0;
  const millis = Number(match[4].padEnd(3, '0'));
  return ((hours * 60 + Number(match[2])) * 60 + Number(match[3])) * 1000 + millis;
}

/** Cues from a VTT or SRT file, in the file's own time (source time). Bad blocks are skipped. */
export function parseCaptionFile(text: string): Array<{ startMs: number; endMs: number; lines: string[] }> {
  const blocks = text.replace(/\r\n?/g, '\n').replace(/^﻿/, '').split(/\n{2,}/);
  const cues: Array<{ startMs: number; endMs: number; lines: string[] }> = [];
  blocks.forEach((block) => {
    const lines = block.split('\n').filter((line) => line.length > 0);
    const timingIndex = lines.findIndex((line) => line.includes('-->'));
    if (timingIndex < 0) return;
    const [from, rest] = lines[timingIndex].split('-->');
    const to = (rest || '').trim().split(/\s+/)[0];
    const startMs = parseClock(from);
    const endMs = parseClock(to || '');
    if (startMs === null || endMs === null || endMs <= startMs) return;
    const body = lines.slice(timingIndex + 1).map((line) => line.replace(/<[^>]+>/g, '').trim()).filter(Boolean);
    if (body.length === 0) return;
    cues.push({ startMs, endMs, lines: body });
  });
  return cues;
}

/** Uploaded cues (source time) placed on the result through the segments; a cue is clipped to the segment it starts in. */
export function placeFileCues(cues: Array<{ startMs: number; endMs: number; lines: string[] }>, list: Pick<EditList, 'segments' | 'sourceDurationMs'>): CaptionCue[] {
  const placements = segmentPlacements(list);
  const out: CaptionCue[] = [];
  placements.forEach(({ segment, startMs }) => {
    cues.forEach((cue) => {
      const from = Math.max(cue.startMs, segment.inMs);
      const to = Math.min(cue.endMs, segment.outMs);
      if (to <= from) return;
      out.push({
        startMs: Math.round(startMs + (from - segment.inMs) / segment.speed),
        endMs: Math.round(startMs + (to - segment.inMs) / segment.speed),
        lines: cue.lines,
      });
    });
  });
  return out;
}
